import React, { useState } from "react";

import { useNumberInput } from "../../../../../../hooks/useNumberInput";
import {
  fromDatetimeLocalValue,
  toDatetimeLocalValue,
} from "../../../../../../utils/datetimeLocal";
import { useRouteBuilder } from "../../context/RouteBuilderContext";
import {
  maxRouteDurationHoursFromMinutes,
  maxRouteDurationMinutesFromHours,
} from "../../lib/helpers";
import LocationPicker from "./LocationPicker";
import { DEFAULT_SERVICE_TIME_MINUTES } from "../../lib/generationProperties";

export default function RouteSetupStep({
  maxRouteCount,
  routeCount,
  routeNamePrefix,
  onRouteNameChange,
}: {
  maxRouteCount: number;
  routeCount: number;
  routeNamePrefix: string;
  onRouteNameChange: (value: string) => void;
}) {
  const {
    inputs,
    setInputs,
    locations,
    setSetupLocation,
    saveCompanyLocation,
    isSavingLocation,
  } = useRouteBuilder();
  const incorporateTime = inputs.incorporateTime !== false;
  const sameLocation = inputs.sameStartAndEndLocation !== false;
  const [newAddressLocation, setNewAddressLocation] = useState<
    "start" | "end" | null
  >(null);
  const addingAddressLocation =
    newAddressLocation ?? (locations.length === 0 ? "start" : null);
  const setRouteNameOverride = onRouteNameChange;
  const routeCountInput = useNumberInput({
    value: routeCount,
    onChange: (next) =>
      setInputs((prev) => ({
        ...prev,
        routeCount: next,
      })),
    min: 1,
    max: maxRouteCount,
  });
  const durationInput = useNumberInput({
    value: maxRouteDurationHoursFromMinutes(inputs.maxRouteDurationMinutes),
    onChange: (hours) =>
      setInputs((prev) => ({
        ...prev,
        maxRouteDurationMinutes: maxRouteDurationMinutesFromHours(hours),
      })),
    min: 1,
    maxDecimals: 1,
    step: 0.5,
  });
  const serviceTimeInput = useNumberInput({
    value: inputs.defaultServiceTimeMinutes ?? DEFAULT_SERVICE_TIME_MINUTES,
    onChange: (minutes) =>
      setInputs((prev) => ({
        ...prev,
        defaultServiceTimeMinutes: minutes,
      })),
    min: 1,
  });

  return (
    <>
      <label className="block text-sm font-medium text-slate-700">
        Number of routes
        <input
          type="text"
          inputMode={routeCountInput.inputMode}
          min={1}
          max={maxRouteCount}
          data-testid="route-count-input"
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
          value={routeCountInput.value}
          onChange={routeCountInput.onChange}
          onFocus={routeCountInput.onFocus}
          onBlur={routeCountInput.onBlur}
        />
        <p className="mt-1 text-xs text-slate-500">
          Cannot exceed the number of selected properties.
        </p>
      </label>

      <div className="space-y-3">
        {!addingAddressLocation ? (
          <label className="flex cursor-pointer items-start gap-2.5">
            <input
              type="checkbox"
              className="mt-0.5 h-[18px] w-[18px] shrink-0 cursor-pointer accent-snow-blue-3"
              checked={sameLocation}
              onChange={(event) => {
                const checked = event.target.checked;
                setInputs((prev) => ({
                  ...prev,
                  sameStartAndEndLocation: checked,
                  startLocation: inputs.startLocation,
                  endLocation: checked
                    ? inputs.startLocation
                    : inputs.endLocation,
                }));
              }}
            />
            <span className="block text-sm font-semibold text-slate-800">
              Start and end locations are the same
            </span>
          </label>
        ) : null}
        <div
          className={`grid gap-4 ${sameLocation || addingAddressLocation ? "" : "sm:grid-cols-2"}`}
        >
          {(["start", "end"] as const)
            .filter((type) =>
              addingAddressLocation
                ? type === addingAddressLocation
                : type === "start" || !sameLocation,
            )
            .map((type) => (
              <LocationPicker
                key={type}
                label={type === "start" ? "Start location" : "End location"}
                locations={locations}
                selected={inputs[`${type}Location`]}
                saving={isSavingLocation}
                onAddingAddressChange={(adding) =>
                  setNewAddressLocation(adding ? type : null)
                }
                onSelect={(location) => setSetupLocation(type, location)}
                onSaveNew={async (values) => {
                  const saved = await saveCompanyLocation(values);
                  if (saved) setSetupLocation(type, saved);
                  return saved;
                }}
              />
            ))}
        </div>
      </div>

      <div className="space-y-3">
        <label className="flex cursor-pointer items-start gap-2.5">
          <input
            type="checkbox"
            data-testid="incorporate-time-checkbox"
            className="mt-0.5 h-[18px] w-[18px] shrink-0 cursor-pointer accent-snow-blue-3"
            checked={incorporateTime}
            aria-expanded={incorporateTime}
            onChange={(event) => {
              const checked = event.target.checked;
              setInputs((prev) => ({ ...prev, incorporateTime: checked }));
            }}
          />
          <span>
            <span className="block text-sm font-semibold text-slate-800">
              Incorporate time
            </span>
            <span className="block text-xs text-slate-500">
              Use route length and service time when splitting properties.
            </span>
          </span>
        </label>
        {incorporateTime ? (
          <div
            data-testid="incorporate-time-fields"
            className="ml-7 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3.5"
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm font-medium text-slate-700">
                Max route duration (hours)
                <input
                  type="text"
                  inputMode={durationInput.inputMode}
                  min={1}
                  data-testid="max-route-duration-input"
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2"
                  value={durationInput.value}
                  onChange={durationInput.onChange}
                  onFocus={durationInput.onFocus}
                  onBlur={durationInput.onBlur}
                />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Default service time (minutes)
                <input
                  type="text"
                  inputMode={serviceTimeInput.inputMode}
                  min={1}
                  data-testid="default-service-time-input"
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2"
                  value={serviceTimeInput.value}
                  onChange={serviceTimeInput.onChange}
                  onFocus={serviceTimeInput.onFocus}
                  onBlur={serviceTimeInput.onBlur}
                />
              </label>
            </div>
            <p className="text-xs text-slate-500">
              Default service time for properties with no service history.
              Properties with service history will use their average time.
            </p>
          </div>
        ) : (
          <p
            data-testid="incorporate-time-off-note"
            className="ml-7 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2.5 text-sm text-slate-600"
          >
            Routes split by property count. Max route duration and service time
            are ignored. Drive time still counts and is shown.
          </p>
        )}
      </div>

      {/* TODO: Restore the planned dispatch time field when scheduling is
          exposed in Route Builder again. The internal default remains in use
          for route timing and one-time-route expiration.
      <label className="block text-sm font-medium text-slate-700">
        Planned dispatch time
        <input
          type="datetime-local"
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
          value={toDatetimeLocalValue(inputs.plannedDispatchTime)}
          onChange={(e) => {
            const plannedDispatchTime = fromDatetimeLocalValue(e.target.value);
            setInputs((prev) => ({
              ...prev,
              plannedDispatchTime: plannedDispatchTime ?? "",
            }));
          }}
        />
      </label>
      */}

      {inputs.routeType === "temporary" ? (
        <label className="block text-sm font-medium text-slate-700">
          Expires at
          <input
            type="datetime-local"
            required
            data-testid="temporary-expires-at-input"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
            value={toDatetimeLocalValue(inputs.expiresAt ?? "")}
            onChange={(e) => {
              const expiresAt = fromDatetimeLocalValue(e.target.value);
              setInputs((prev) => ({
                ...prev,
                expiresAt: expiresAt ?? "",
              }));
            }}
          />
        </label>
      ) : null}

      <label className="block text-sm font-medium text-slate-700">
        Route name
        <input
          type="text"
          data-testid="route-name-prefix"
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
          value={routeNamePrefix}
          placeholder="e.g. Snow Crew"
          onChange={(e) => setRouteNameOverride(e.target.value)}
        />
        <p className="mt-1 text-xs text-slate-500">
          Routes are named {routeNamePrefix.trim() || "Crew"} 1,{" "}
          {routeNamePrefix.trim() || "Crew"} 2, … after generate. You can rename
          individual routes later.
        </p>
      </label>
    </>
  );
}
